import React, { useState } from 'react';
import { Switch, IconButton, Icon } from '@mui/material';
import { useUpdatePartnerTask, useDeletePartnerTask } from 'queries/prompts';
import DeleteConfirm from '../customPrompts/ModelPrompts/DeleteConfirm';

const Tag = ({ text, color = '#F4F7F9' }) => (
  <span style={{
    background: color,
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#495057',
    marginRight: '8px',
    marginTop: '8px',
    display: 'inline-block',
  }}>
    {text}
  </span>
);

// Flat card-per-task list for super-admin tasks, mirroring customPrompts/Prompts.js
// (agencyiframe's Partner Tasks list) but scoped to type "SuperAdmin".
function Prompts({ tasks, refresh, roleFilter, sectorFilter, searchQuery, jobRolesList, sectorsList, categoriesList, integrationsList }) {
  const [editTask, setEditTask] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Both hooks already invalidate the shared partner-tasks query on success;
  // `refresh?.()` is called manually below to preserve this screen's existing
  // extra behavior (re-fetching via the parent-owned refetch too).
  const { mutateAsync: toggleActiveMutation } = useUpdatePartnerTask();
  const { mutateAsync: deletePromptMutation } = useDeletePartnerTask();

  const getLabel = (id, list) => {
    const item = (list || []).find((l) => l.value === id);
    return item ? item.label : id;
  };

  const getCategoryLabel = (category) => {
    const first = String(category || '').split(',')[0].trim();
    if (!first) return null;
    const item = categoriesList.find((c) => c.value === first || (c.label || '').toLowerCase() === first.toLowerCase());
    return item ? item.label : null;
  };

  const toggleActive = async (task) => {
    const active = !task.active;
    await toggleActiveMutation({ prompt_id: task._id, active });
    refresh?.();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deletePromptMutation(deleteTarget._id);
    setDeleteTarget(null);
    refresh?.();
  };

  const filteredTasks = (tasks || []).filter((task) => {
    const roleMatch = roleFilter === 'all' || task.job_roles?.includes(roleFilter);
    const sectorMatch = sectorFilter === 'all' || task.sectors?.includes(sectorFilter);
    const searchMatch = !searchQuery || task.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return roleMatch && sectorMatch && searchMatch;
  });

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filteredTasks.map((task) => (
          <div key={task._id} style={{
            background: '#FFFFFF',
            border: '1px solid #E9ECEF',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A1A', margin: '0 0 10px 0' }}>{task.title}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {getCategoryLabel(task.category) && <Tag text={getCategoryLabel(task.category)} />}
                {task.job_roles?.map((roleId) => (
                  <Tag key={roleId} text={getLabel(roleId, jobRolesList)} />
                ))}
                {task.sectors?.map((sectorId) => (
                  <Tag key={sectorId} text={getLabel(sectorId, sectorsList)} />
                ))}
                {task.integrations?.map((intId) => (
                  <Tag key={intId} text={getLabel(intId, integrationsList)} color="#F1F3F5" />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={Boolean(task.active)}
                onChange={() => toggleActive(task)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#000' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#000' },
                }}
              />
            </div>
          </div>
        ))}
        {filteredTasks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6C757D' }}>
            No tasks found for the selected criteria.
          </div>
        )}
      </div>

      {deleteTarget && (
        <DeleteConfirm
          openModel={Boolean(deleteTarget)}
          onclosemodel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

export default Prompts;
